import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Truck,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Footer } from "@/components/Footer";

const RESTAURANT_LOGO = "https://customer-assets.emergentagent.com/job_taco-tracker-4/artifacts/49jtitvp_2NARANJAlogo%20ordenado%20definitivo.png";

export const DriverLayout = ({ children, title }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const navItems = [
    { path: "/driver", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/driver/orders", icon: Truck, label: "Pedidos" },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-black border-b border-gray-800 z-40 flex items-center px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(true)}
          className="text-green-500"
          data-testid="driver-menu-toggle"
        >
          <Menu className="w-6 h-6" />
        </Button>
        <h1 className="ml-4 font-heading font-bold text-lg text-green-500">{title}</h1>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/70 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 h-full w-64 bg-black border-r border-gray-800 z-50 transition-transform duration-300 lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-gray-800">
            <Link to="/driver" className="flex items-center gap-2">
              <img src={RESTAURANT_LOGO} alt="Logo" className="w-10 h-10 rounded-lg" />
              <div>
                <span className="font-heading font-bold text-green-500 text-sm">Conexo POS</span>
                <span className="block text-xs text-gray-500">Repartidor</span>
              </div>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-gray-400"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`driver-nav-${item.label.toLowerCase()}`}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors",
                    isActive
                      ? "bg-green-500 text-black"
                      : "text-gray-400 hover:bg-gray-900 hover:text-green-500"
                  )}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-gray-800">
            <div className="flex items-center gap-3 mb-4">
              <img
                src={user?.picture || RESTAURANT_LOGO}
                alt={user?.name}
                className="w-10 h-10 rounded-full border border-gray-800"
              />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-white truncate">{user?.name || "Repartidor"}</p>
                <p className="text-xs text-gray-500">Repartidor</p>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              className="w-full border-gray-700 text-gray-300 hover:text-red-500 hover:border-red-500 transition-colors"
              onClick={handleLogout}
              data-testid="driver-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen bg-gray-950 flex flex-col">
        <div className="p-4 lg:p-8 flex-1">{children}</div>
        <Footer />
      </main>
    </div>
  );
};

export default DriverLayout;
